import {
  ApplicationConfig,
  provideZoneChangeDetection,
  APP_INITIALIZER,
  ErrorHandler,
  inject,
  provideAppInitializer,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideTranslateLoader, provideTranslateService } from '@ngx-translate/core';
import { provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideToastr } from 'ngx-toastr';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { DdMmYyyyDateAdapter } from './core/utils/dd-mm-yyyy-date-adapter';

import { routes } from './app.routes';
import { appReducers, appMetaReducers } from './core/store/app.state';
import { UserEffects } from './core/store/user/user.effects';
import { ToastEffects } from './core/store/toast/toast.effects';
import { NotificationEffects } from './core/store/notification/notification.effects';
import { ManagementCompanyEffects } from './core/store/management-company/management-company.effects';
import { FundEffects } from './core/store/fund/fund.effects';
import { BoardMemberEffects } from './core/store/board-member/board-member.effects';
import { ProcessEffects } from './core/store/process/process.effects';
import { ClientInvitationEffects } from './core/store/management-company/client-invitation.effects';
import { CountryEffects } from './core/store/country/country.effects';
import { environment } from '../environments/environment';
import { ApiConfiguration } from './core/api/api-configuration';
import { correlationIdInterceptor } from './core/interceptors/correlation-id.interceptor';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { languageInterceptor } from './core/interceptors/language.interceptor';
import { LanguageEffects } from './core/store/language/language.effects';
import { UserSignatureEffects } from './core/store/user-signature/user-signature.effects';
import { initializeAuth } from './core/initializers/auth.initializer';
import { RoutingErrorHandlerService } from './core/services/routing-error-handler.service';
import { VersionCheckService } from './shared/notifications/version-check.service';
import { GlobalErrorHandler } from './core/handlers/global-error.handler';
import { ThemeService } from './core/services/theme.service';
import { DEFAULT_LANGUAGE, DEFAULT_LANGUAGE_CONFIG } from './core/i18n/languages.config';
import { LanguageService } from './core/i18n/language.service';
import { FeatureTranslateLoader } from './core/i18n/feature-translate.loader';

// AG Grid module registration (REQUIRED for AG Grid v35+)
import {AllCommunityModule, ModuleRegistry, provideGlobalGridOptions} from 'ag-grid-community';

ModuleRegistry.registerModules([AllCommunityModule]);

// Use legacy theming so the CSS imports (ag-grid.css + ag-theme-material.css) work correctly
provideGlobalGridOptions({theme: "legacy"});

// ── Material Datepicker — display/parse in dd/MM/yyyy across the app ──
const APP_DATE_FORMATS = {
  parse: { dateInput: 'dd/MM/yyyy' },
  display: {
    dateInput: 'dd/MM/yyyy',
    monthYearLabel: 'MMM yyyy',
    dateA11yLabel: 'dd/MM/yyyy',
    monthYearA11yLabel: 'MMMM yyyy',
  },
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({eventCoalescing: true}),
    provideRouter(routes),
    provideAnimationsAsync(),
    provideHttpClient(withInterceptors([correlationIdInterceptor, languageInterceptor, authInterceptor])),

    // ── Material Datepicker (dd/MM/yyyy, default language locale) ──
    { provide: MAT_DATE_LOCALE, useValue: DEFAULT_LANGUAGE_CONFIG.dateLocale },
    { provide: MAT_DATE_FORMATS, useValue: APP_DATE_FORMATS },
    { provide: DateAdapter, useClass: DdMmYyyyDateAdapter },

    // ── API Configuration ──
    {
      provide: ApiConfiguration,
      useFactory: () => {
        const config = new ApiConfiguration();
        config.rootUrl = environment.apiUrl;
        return config;
      },
    },

    // ── i18n ──
    // One file per feature: assets/i18n/<lang>/<file>.json (see TRANSLATION_FILES)
    provideTranslateService({
      fallbackLang: DEFAULT_LANGUAGE,
      loader: provideTranslateLoader(FeatureTranslateLoader),
    }),

    // ── NGRX ──
    provideStore(appReducers, { metaReducers: appMetaReducers }),
    provideEffects([UserEffects, ToastEffects, NotificationEffects, ManagementCompanyEffects, FundEffects, BoardMemberEffects, ProcessEffects, ClientInvitationEffects, CountryEffects, LanguageEffects, UserSignatureEffects]),
    // Never in production: the store holds the KYC forms, bank account numbers included (US-007, AC-21).
    ...(environment.production ? [] : [provideStoreDevtools({maxAge: 25, logOnly: false})]),

    // ── Toastr (custom toast component is provided per-call by NotificationService) ──
    // NotificationService.show() overrides timeOut to 5000 for success/info/warning
    // and to 0 (sticky) for errors. These globals are the fallback for direct
    // ToastrService calls only.
    provideToastr({
      positionClass: 'toast-top-right',
      preventDuplicates: true,
      progressBar: false,
      closeButton: false,
      timeOut: 4500,
      extendedTimeOut: 2000,
      tapToDismiss: false,
    }),

    // ── Global Error Handler ──
    { provide: ErrorHandler, useClass: GlobalErrorHandler },

    // ── Theme bootstrap (load themes before app renders to avoid FOUC) ──
    provideAppInitializer(() => {
      const themeService = inject(ThemeService);
      return themeService.loadThemes();
    }),

    // ── Language bootstrap (saved preference → browser language → default) ──
    provideAppInitializer(() => inject(LanguageService).init()),

    // ── Authentication Initializer ──
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAuth,
      multi: true,
    },

    // ── Routing Error Handler ──
    {
      provide: APP_INITIALIZER,
      useFactory: (handler: RoutingErrorHandlerService) => () => {
        handler.init();
        return Promise.resolve();
      },
      deps: [RoutingErrorHandlerService],
      multi: true,
    },

    // ── "New version available" notice (123y1w8rpab) ──
    provideAppInitializer(() => inject(VersionCheckService).init()),
  ],
};
